import { Menu } from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/utils'
import { Wordmark } from '../brand/Wordmark'
import { Button } from '../ui/Button'
import { ThemeToggle } from './ThemeToggle'
import { Sidebar, type RecentItem } from './Sidebar'
import { TopBar, type TopBarContent } from './TopBar'

interface AppShellProps {
  recent: RecentItem[]
  activeId?: string
  onSelect?: (id: string) => void
  onRename?: (id: string, title: string) => void
  onDelete?: (id: string) => void
  route: string
  /** The single top bar's contents (breadcrumbs + context + actions). An
   * explicit prop wins; otherwise a page registers its own via `useTopBar`. */
  topbar?: TopBarContent
  /** Pinned below the scrolling canvas, inside the content column (e.g. the
   * playback transport bar) - sits right of the sidebar and never overlaps. */
  footer?: React.ReactNode
  children: React.ReactNode
}

// A page-owned slot for the single top bar: a page deep in the tree (e.g. the
// live session) declares its breadcrumbs/labels/actions via `useTopBar`, which
// renders into the shell's one bar instead of stacking a second header below it.
const TopBarSlot = React.createContext<{
  setContent: (content: TopBarContent | undefined) => void
}>({ setContent: () => {} })

/** Register this page's top-bar content for as long as the component is mounted.
 * Pass a memoized `content` so the bar only re-renders when it actually changes. */
export function useTopBar(content: TopBarContent): void {
  const { setContent } = React.useContext(TopBarSlot)
  React.useEffect(() => {
    setContent(content)
    return () => setContent(undefined)
  }, [setContent, content])
}

// App shell: the sidebar is hidden by default and toggled from the top bar's
// menu icon. On desktop (md+) it opens as a DOCKED drawer that pushes the canvas
// right (no overlap); below md it opens as an off-canvas overlay. The canvas
// goes edge-to-edge with px-4 gutters.
export function AppShell({
  recent,
  activeId,
  onSelect,
  onRename,
  onDelete,
  route,
  topbar,
  footer,
  children
}: AppShellProps) {
  const [drawerOpen, setDrawerOpen] = React.useState(false)
  const [slotContent, setSlotContent] = React.useState<TopBarContent>()
  const slot = React.useMemo(() => ({ setContent: setSlotContent }), [])

  // Close the OVERLAY (mobile) on route change; the desktop docked drawer
  // persists across navigation. Escape closes either.
  React.useEffect(() => {
    if (window.matchMedia('(min-width: 768px)').matches) return
    setDrawerOpen(false)
  }, [route])
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) =>
      e.key === 'Escape' && setDrawerOpen(false)
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  return (
    <div className="flex h-dvh w-full overflow-hidden bg-bg text-text">
      {/* Desktop (md+): a DOCKED drawer — a normal flex sibling that pushes the
          canvas right, so it never overlaps content. Hidden until toggled. */}
      {drawerOpen && (
        <div className="hidden shrink-0 md:block">
          <Sidebar
            recent={recent}
            activeId={activeId}
            route={route}
            onSelect={onSelect}
            onRename={onRename}
            onDelete={onDelete}
          />
        </div>
      )}

      {/* Mobile (below md): an off-canvas overlay over the canvas. */}
      {drawerOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <button
            type="button"
            aria-label="Close menu"
            className="absolute inset-0 bg-text/30"
            onClick={() => setDrawerOpen(false)}
          />
          <div className="absolute inset-y-0 left-0 shadow-(--shadow-md)">
            <Sidebar
              recent={recent}
              activeId={activeId}
              route={route}
              onSelect={onSelect}
              onRename={onRename}
              onDelete={onDelete}
            />
          </div>
        </div>
      )}

      {/* Main canvas */}
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar
          content={topbar ?? slotContent}
          leading={
            <>
              <Button
                variant="ghost"
                size="icon"
                aria-label={drawerOpen ? 'Collapse menu' : 'Expand menu'}
                aria-pressed={drawerOpen}
                onClick={() => setDrawerOpen((v) => !v)}
              >
                <Menu />
              </Button>
              <Wordmark size="small" />
            </>
          }
          trailing={<ThemeToggle />}
        />

        <main className="min-h-0 flex-1 overflow-y-auto">
          <TopBarSlot.Provider value={slot}>{children}</TopBarSlot.Provider>
        </main>
        {footer}
      </div>
    </div>
  )
}

/** Centered reading column capped at the reading max-width. */
export function ReadingColumn({
  className,
  children
}: {
  className?: string
  children: React.ReactNode
}) {
  return (
    <div
      className={cn(
        'mx-auto w-full max-w-reading px-4 py-10 md:px-6',
        className
      )}
    >
      {children}
    </div>
  )
}
